/* configurator.js - model-first configurator (Brouwers-aanlevering 2026-06-09)
 * Stap 1 = Model (Barcelona/Marbella/Sevilla/Valencia). Daarna model-afhankelijke
 * stappen: hoofdbord · stof · poten · topper · matras · maat.
 * Prijzen: formule-gedreven per model (sheet Brouwers 2026-07); zie het pricing-blok hieronder.
 * - State-persist in URL (deelbare config-link, ?model=…&hoofdbord=…)
 * - Model-hero swap + stof-swatch in summary
 * - GA4 events: view_item, add_to_cart
 */
(() => {
  'use strict';
  const container = document.querySelector('.configurator');
  if (!container) return;

  // ── Models (vanaf-prijzen bevestigd door Brouwers; baseline = kleinste maat + standaard) ──
  // LET OP: `defaults` is alleen documentatie van de standaarduitvoering. Sinds 2026-07-05
  // wordt er NIETS voorgeselecteerd: de klant kiest elke stap zelf en het prijsvak toont
  // "vanaf € <base>" tot de samenstelling compleet is.
  const MODELS = {
    barcelona: {
      name: 'Boxspring Barcelona', tag: 'Opberg', base: 1250, baseMaat: '140x200',
      steps: ['hoofdbord', 'stof', 'poten', 'matras', 'size'],
      defaults: { hoofdbord: 'vlak', stof: 'cayenne-2', poten: 'rond6', matras: 'ongestoffeerd', size: '140x200' },
      spec: 'Opbergbox met klep (30 cm) · ~900 l opbergruimte',
      // Brouwers 2026-08-12: geen LED-hoofdbord op de opbergbox. De klep van de
      // opbergbox draait langs het hoofdbord omhoog, en de lampen zouden daarbij
      // kunnen sneuvelen. De optie wordt daarom verborgen i.p.v. uitgeschakeld.
      // Brouwers 2026-08-13: zonder toelichting op de pagina, de optie is er
      // gewoon niet.
      hoofdbordUit: ['banen_led'],
    },
    marbella: {
      name: 'Boxspring Marbella', tag: 'Klassiek', base: 1650, baseMaat: '140x200',
      steps: ['hoofdbord', 'stof', 'poten', 'topper', 'size'],
      defaults: { hoofdbord: 'vlak', stof: 'cayenne-1112', poten: 'rond6', topper: 'stockholm', size: '140x200' },
      spec: 'Box 30 cm · gestoffeerd matras inbegrepen · 7-zone pocketvering',
    },
    sevilla: {
      name: 'Boxspring Sevilla', tag: 'Luxe', base: 2795, baseMaat: '140x200', ledIncluded: true,
      steps: ['hoofdbord', 'stof', 'poten', 'topper', 'size'],
      defaults: { hoofdbord: 'banen_led', stof: 'cayenne-1112', poten: 'highheel', topper: 'biokatoen', size: '140x200' },
      spec: 'Box 20 cm · matras inbegrepen · bio-katoen topper · LED-hoofdbord',
    },
    valencia: {
      name: 'Ledikant Valencia', tag: 'Statement', base: 1750, baseMaat: '140x200',
      steps: ['matras', 'size'], sizes: ['140x200', '160x200', '180x200'],
      defaults: { matras: 'ongestoffeerd', size: '160x200' },
      spec: 'Uniek afgerond hoofdbord · slimme opbergruimte · vaste stoffering',
    },
  };

  // ── Pricing (sheet-model Brouwers 2026-07) ──────────────────────────────────
  // De getoonde prijs komt uit onderstaande formule. In Fase 3 worden de Shopify-
  // varianten met exact deze bedragen (zelfde afronding) gegenereerd, zodat de
  // mandje-prijs per constructie gelijk is aan de weergave.
  //   bedprijs = round5( lengtefactor(lengte) · ( base(model,breedte) + hoofdbord + matras ) )
  //   poten    = +€100 bij high-heel, als LOSSE cart-regel (buiten de lengte-%)
  const MODEL_BASE = {
    marbella:  { 140: 1650, 160: 1750, 180: 1850, 200: 1950 },
    barcelona: { 140: 1250, 160: 1350, 180: 1450, 200: 1550 },
    sevilla:   { 140: 2795, 160: 2895, 180: 2995, 200: 3095 },
    valencia:  { 140: 1750, 160: 1850, 180: 1950 },
  };
  // Matras-meerprijs. Marbella/Sevilla: matras inbegrepen (geen matras-stap) → 0.
  // Barcelona: maat-afhankelijk; Valencia: +200 vast. Alleen 'gestoffeerd' rekent meer.
  const MODEL_MATRAS = {
    barcelona: { 140: 500, 160: 600, 180: 700, 200: 800 },
    valencia:  { 140: 200, 160: 200, 180: 200 },
  };
  // Hoofdbord t.o.v. de basisuitvoering van het model. Sevilla heeft LED als basis.
  const HOOFDBORD_DELTA = {
    marbella:  { vlak: 0, banen: 250, banen_led: 450 },
    barcelona: { vlak: 0, banen: 250, banen_led: 450 },
    sevilla:   { banen_led: 0, banen: -250, vlak: -450 },
  };
  const LENGTE_FACTOR = { '200': 1, '210': 1.10, '220': 1.15 };
  const POTEN_MEERPRIJS = 100; // alleen high-heel

  const round5 = (n) => Math.round(n / 5) * 5;

  // maat "140x210" → { breedte: 140, lengte: '210' }
  function splitMaat(maat) {
    const [w, l] = String(maat || '').split('x');
    return { breedte: parseInt(w, 10), lengte: l };
  }

  // Welke maten voert dit model? M/B/S = 140/160/180/200 × lengte 200/210/220;
  // Valencia = 140/160/180, alleen x200 (geen lengtematen).
  function modelAllowsSize(model, maat) {
    const { breedte, lengte } = splitMaat(maat);
    if (!MODEL_BASE[model] || MODEL_BASE[model][breedte] == null) return false;
    if (model === 'valencia') return lengte === '200';
    return ['200', '210', '220'].includes(lengte);
  }

  // Bedprijs (excl. poten) voor een samenstelling; null als de maat niet bij het model hoort.
  function bedPrice(model, maat, hoofdbord, matras) {
    const m = MODELS[model];
    if (!m || !maat) return null;
    const { breedte, lengte } = splitMaat(maat);
    const base = MODEL_BASE[model] && MODEL_BASE[model][breedte];
    if (base == null) return null;
    const factor = LENGTE_FACTOR[lengte] != null ? LENGTE_FACTOR[lengte] : 1;
    let hb = 0;
    if (m.steps.includes('hoofdbord')) {
      const hbKey = hoofdbord || (m.ledIncluded ? 'banen_led' : 'vlak');
      hb = (HOOFDBORD_DELTA[model] && HOOFDBORD_DELTA[model][hbKey]) || 0;
    }
    let matrasAdd = 0;
    if (MODEL_MATRAS[model] && matras === 'gestoffeerd') {
      matrasAdd = MODEL_MATRAS[model][breedte] || 0;
    }
    return round5(factor * (base + hb + matrasAdd));
  }

  // Poten-meerprijs voor de getoonde totaalprijs (in Fase 3 een losse cart-regel).
  function potenAdd(model, poten) {
    const m = MODELS[model];
    return (m && m.steps.includes('poten') && poten === 'highheel') ? POTEN_MEERPRIJS : 0;
  }

  // ── Display-labels ──
  const LABELS = {
    hoofdbord: { vlak: 'Glad hoofdbord', banen: 'Banen-hoofdbord', banen_led: 'Banen + LED' },
    stof: {
      'cayenne-2': 'Cayenne New White', 'cayenne-1112': 'Cayenne Cream', 'cayenne-1126': 'Cayenne Light Beige',
      'cayenne-1124': 'Cayenne Biscuit', 'cayenne-1132': 'Cayenne Silver', 'cayenne-1122': 'Cayenne Elephant',
      'inari-22': 'Inari 22', 'inari-26': 'Inari 26', 'inari-90': 'Inari 90',
      'inari-34': 'Inari 34', 'inari-72': 'Inari 72', 'inari-91': 'Inari 91',
    },
    poten: { rond6: '6 cm rond, zwart hout', slede6: '6 cm slede, zwart', rond12: '12 cm rond, zwart hout', highheel: '15 cm high heel, metaal' },
    topper: { stockholm: 'Stockholm topper', biokatoen: 'Bio-katoen topper', micropocket: 'Micropocket topper' },
    matras: { gestoffeerd: 'Met matras (gestoffeerd)', ongestoffeerd: 'Zonder matras (eigen)' },
    size: {
      '140x200': '140 × 200 cm', '140x210': '140 × 210 cm', '140x220': '140 × 220 cm',
      '160x200': '160 × 200 cm', '160x210': '160 × 210 cm', '160x220': '160 × 220 cm',
      '180x200': '180 × 200 cm', '180x210': '180 × 210 cm', '180x220': '180 × 220 cm',
      '200x200': '200 × 200 cm', '200x210': '200 × 210 cm', '200x220': '200 × 220 cm',
    },
    model: { barcelona: 'Boxspring Barcelona', marbella: 'Boxspring Marbella', sevilla: 'Boxspring Sevilla', valencia: 'Ledikant Valencia' },
  };
  const STEP_TITLES = { model: 'Model', hoofdbord: 'Hoofdbord', stof: 'Stof', poten: 'Poten', topper: 'Topper', matras: 'Matras', size: 'Maat' };

  // ── Variant-lookup → add-to-cart ─────────────────────────────
  // Model-gekeyde variant-map: "model|maat|hoofdbord|matras" → Shopify variant_id.
  // Wordt in Fase 3 gevuld met de gegenereerde variant-ID's (4 producten-per-model).
  // Zolang leeg valt add-to-cart terug op de "op aanvraag"-melding: staging is
  // niet-transacterend tot de varianten live staan. Matras = 'incl' voor Marbella/
  // Sevilla (inbegrepen), 'gestoffeerd'/'ongestoffeerd' voor Barcelona/Valencia;
  // hoofdbord = 'na' als het model geen hoofdbord-stap heeft (Valencia).
  const VARIANT_LOOKUP = {
    "barcelona|140x200|banen_led|gestoffeerd": 58511715369285,
    "barcelona|140x200|banen_led|ongestoffeerd": 58511715402053,
    "barcelona|140x200|banen|gestoffeerd": 58511715303749,
    "barcelona|140x200|banen|ongestoffeerd": 58511715336517,
    "barcelona|140x200|vlak|gestoffeerd": 58511715238213,
    "barcelona|140x200|vlak|ongestoffeerd": 58511715270981,
    "barcelona|140x210|banen_led|gestoffeerd": 58511715565893,
    "barcelona|140x210|banen_led|ongestoffeerd": 58511715598661,
    "barcelona|140x210|banen|gestoffeerd": 58511715500357,
    "barcelona|140x210|banen|ongestoffeerd": 58511715533125,
    "barcelona|140x210|vlak|gestoffeerd": 58511715434821,
    "barcelona|140x210|vlak|ongestoffeerd": 58511715467589,
    "barcelona|140x220|banen_led|gestoffeerd": 58511715762501,
    "barcelona|140x220|banen_led|ongestoffeerd": 58511715795269,
    "barcelona|140x220|banen|gestoffeerd": 58511715696965,
    "barcelona|140x220|banen|ongestoffeerd": 58511715729733,
    "barcelona|140x220|vlak|gestoffeerd": 58511715631429,
    "barcelona|140x220|vlak|ongestoffeerd": 58511715664197,
    "barcelona|160x200|banen_led|gestoffeerd": 58511715959109,
    "barcelona|160x200|banen_led|ongestoffeerd": 58511715991877,
    "barcelona|160x200|banen|gestoffeerd": 58511715893573,
    "barcelona|160x200|banen|ongestoffeerd": 58511715926341,
    "barcelona|160x200|vlak|gestoffeerd": 58511715828037,
    "barcelona|160x200|vlak|ongestoffeerd": 58511715860805,
    "barcelona|160x210|banen_led|gestoffeerd": 58511716155717,
    "barcelona|160x210|banen_led|ongestoffeerd": 58511716188485,
    "barcelona|160x210|banen|gestoffeerd": 58511716090181,
    "barcelona|160x210|banen|ongestoffeerd": 58511716122949,
    "barcelona|160x210|vlak|gestoffeerd": 58511716024645,
    "barcelona|160x210|vlak|ongestoffeerd": 58511716057413,
    "barcelona|160x220|banen_led|gestoffeerd": 58511716352325,
    "barcelona|160x220|banen_led|ongestoffeerd": 58511716385093,
    "barcelona|160x220|banen|gestoffeerd": 58511716286789,
    "barcelona|160x220|banen|ongestoffeerd": 58511716319557,
    "barcelona|160x220|vlak|gestoffeerd": 58511716221253,
    "barcelona|160x220|vlak|ongestoffeerd": 58511716254021,
    "barcelona|180x200|banen_led|gestoffeerd": 58511716548933,
    "barcelona|180x200|banen_led|ongestoffeerd": 58511716581701,
    "barcelona|180x200|banen|gestoffeerd": 58511716483397,
    "barcelona|180x200|banen|ongestoffeerd": 58511716516165,
    "barcelona|180x200|vlak|gestoffeerd": 58511716417861,
    "barcelona|180x200|vlak|ongestoffeerd": 58511716450629,
    "barcelona|180x210|banen_led|gestoffeerd": 58511716745541,
    "barcelona|180x210|banen_led|ongestoffeerd": 58511716778309,
    "barcelona|180x210|banen|gestoffeerd": 58511716680005,
    "barcelona|180x210|banen|ongestoffeerd": 58511716712773,
    "barcelona|180x210|vlak|gestoffeerd": 58511716614469,
    "barcelona|180x210|vlak|ongestoffeerd": 58511716647237,
    "barcelona|180x220|banen_led|gestoffeerd": 58511716942149,
    "barcelona|180x220|banen_led|ongestoffeerd": 58511716974917,
    "barcelona|180x220|banen|gestoffeerd": 58511716876613,
    "barcelona|180x220|banen|ongestoffeerd": 58511716909381,
    "barcelona|180x220|vlak|gestoffeerd": 58511716811077,
    "barcelona|180x220|vlak|ongestoffeerd": 58511716843845,
    "barcelona|200x200|banen_led|gestoffeerd": 58511717138757,
    "barcelona|200x200|banen_led|ongestoffeerd": 58511717171525,
    "barcelona|200x200|banen|gestoffeerd": 58511717073221,
    "barcelona|200x200|banen|ongestoffeerd": 58511717105989,
    "barcelona|200x200|vlak|gestoffeerd": 58511717007685,
    "barcelona|200x200|vlak|ongestoffeerd": 58511717040453,
    "barcelona|200x210|banen_led|gestoffeerd": 58511717335365,
    "barcelona|200x210|banen_led|ongestoffeerd": 58511717368133,
    "barcelona|200x210|banen|gestoffeerd": 58511717269829,
    "barcelona|200x210|banen|ongestoffeerd": 58511717302597,
    "barcelona|200x210|vlak|gestoffeerd": 58511717204293,
    "barcelona|200x210|vlak|ongestoffeerd": 58511717237061,
    "barcelona|200x220|banen_led|gestoffeerd": 58511717531973,
    "barcelona|200x220|banen_led|ongestoffeerd": 58511717564741,
    "barcelona|200x220|banen|gestoffeerd": 58511717466437,
    "barcelona|200x220|banen|ongestoffeerd": 58511717499205,
    "barcelona|200x220|vlak|gestoffeerd": 58511717400901,
    "barcelona|200x220|vlak|ongestoffeerd": 58511717433669,
    "marbella|140x200|banen_led|incl": 58511713960261,
    "marbella|140x200|banen|incl": 58511713927493,
    "marbella|140x200|vlak|incl": 58511713894725,
    "marbella|140x210|banen_led|incl": 58511714058565,
    "marbella|140x210|banen|incl": 58511714025797,
    "marbella|140x210|vlak|incl": 58511713993029,
    "marbella|140x220|banen_led|incl": 58511714156869,
    "marbella|140x220|banen|incl": 58511714124101,
    "marbella|140x220|vlak|incl": 58511714091333,
    "marbella|160x200|banen_led|incl": 58511714255173,
    "marbella|160x200|banen|incl": 58511714222405,
    "marbella|160x200|vlak|incl": 58511714189637,
    "marbella|160x210|banen_led|incl": 58511714353477,
    "marbella|160x210|banen|incl": 58511714320709,
    "marbella|160x210|vlak|incl": 58511714287941,
    "marbella|160x220|banen_led|incl": 58511714451781,
    "marbella|160x220|banen|incl": 58511714419013,
    "marbella|160x220|vlak|incl": 58511714386245,
    "marbella|180x200|banen_led|incl": 58511714550085,
    "marbella|180x200|banen|incl": 58511714517317,
    "marbella|180x200|vlak|incl": 58511714484549,
    "marbella|180x210|banen_led|incl": 58511714648389,
    "marbella|180x210|banen|incl": 58511714615621,
    "marbella|180x210|vlak|incl": 58511714582853,
    "marbella|180x220|banen_led|incl": 58511714746693,
    "marbella|180x220|banen|incl": 58511714713925,
    "marbella|180x220|vlak|incl": 58511714681157,
    "marbella|200x200|banen_led|incl": 58511714844997,
    "marbella|200x200|banen|incl": 58511714812229,
    "marbella|200x200|vlak|incl": 58511714779461,
    "marbella|200x210|banen_led|incl": 58511714943301,
    "marbella|200x210|banen|incl": 58511714910533,
    "marbella|200x210|vlak|incl": 58511714877765,
    "marbella|200x220|banen_led|incl": 58511715041605,
    "marbella|200x220|banen|incl": 58511715008837,
    "marbella|200x220|vlak|incl": 58511714976069,
    "sevilla|140x200|banen_led|incl": 58511718154565,
    "sevilla|140x200|banen|incl": 58511718187333,
    "sevilla|140x200|vlak|incl": 58511718220101,
    "sevilla|140x210|banen_led|incl": 58511718252869,
    "sevilla|140x210|banen|incl": 58511718285637,
    "sevilla|140x210|vlak|incl": 58511718318405,
    "sevilla|140x220|banen_led|incl": 58511718351173,
    "sevilla|140x220|banen|incl": 58511718383941,
    "sevilla|140x220|vlak|incl": 58511718416709,
    "sevilla|160x200|banen_led|incl": 58511718449477,
    "sevilla|160x200|banen|incl": 58511718482245,
    "sevilla|160x200|vlak|incl": 58511718515013,
    "sevilla|160x210|banen_led|incl": 58511718547781,
    "sevilla|160x210|banen|incl": 58511718580549,
    "sevilla|160x210|vlak|incl": 58511718613317,
    "sevilla|160x220|banen_led|incl": 58511718646085,
    "sevilla|160x220|banen|incl": 58511718678853,
    "sevilla|160x220|vlak|incl": 58511718711621,
    "sevilla|180x200|banen_led|incl": 58511718744389,
    "sevilla|180x200|banen|incl": 58511718777157,
    "sevilla|180x200|vlak|incl": 58511718809925,
    "sevilla|180x210|banen_led|incl": 58511718842693,
    "sevilla|180x210|banen|incl": 58511718875461,
    "sevilla|180x210|vlak|incl": 58511718908229,
    "sevilla|180x220|banen_led|incl": 58511718940997,
    "sevilla|180x220|banen|incl": 58511718973765,
    "sevilla|180x220|vlak|incl": 58511719006533,
    "sevilla|200x200|banen_led|incl": 58511719039301,
    "sevilla|200x200|banen|incl": 58511719072069,
    "sevilla|200x200|vlak|incl": 58511719104837,
    "sevilla|200x210|banen_led|incl": 58511719137605,
    "sevilla|200x210|banen|incl": 58511719170373,
    "sevilla|200x210|vlak|incl": 58511719203141,
    "sevilla|200x220|banen_led|incl": 58511719235909,
    "sevilla|200x220|banen|incl": 58511719268677,
    "sevilla|200x220|vlak|incl": 58511719301445,
    "valencia|140x200|na|gestoffeerd": 58511724380485,
    "valencia|140x200|na|ongestoffeerd": 58511724413253,
    "valencia|160x200|na|gestoffeerd": 58511724446021,
    "valencia|160x200|na|ongestoffeerd": 58511724478789,
    "valencia|180x200|na|gestoffeerd": 58511724511557,
    "valencia|180x200|na|ongestoffeerd": 58511724544325,
  };

  // High-heel poten = los add-on-product (+€100). variant_id volgt in Fase 3.
  const POTEN_ADDON_VARIANT_ID = 58511724806469;

  // ── Eén-product-samenstelling ───────────────────────────────────────────────
  // De geconfigureerde boxspring komt als ÉÉN line-item in het mandje (de echte
  // maat-variant). Alle keuzes (opbergbox, hoofdbord, stof, poten, topper, matras,
  // maat) reizen mee als line-item-properties en worden in de cart getoond als
  // "Samenstelling". _model + _img (verborgen) sturen de titel + modelfoto.
  // Hoofdbord/LED + matras zitten al in de bedprijs (bedPrice); high-heel poten = losse regel.
  const ADDON_DIMS = ['stof', 'poten', 'topper'];

  // Model → Onderbox (alleen voor het LABEL in de cart-samenstelling, niet voor de prijs):
  //   barcelona → opberg · marbella → box30 · sevilla → box20 · valencia → box30 (ledikant)
  const MODEL_ONDERBOX = { barcelona: 'opberg', marbella: 'box30', sevilla: 'box20', valencia: 'box30' };
  // LET OP: deze waarden moeten LETTERLIJK gelijk zijn aan de radiowaarden in
  // templates/cart.liquid en aan die in assets/cart-drawer.js. Ze reizen als cart-attribuut
  // `Levering` mee, en een waarde die daar niet voorkomt zorgt dat er in de winkelwagen
  // GEEN enkele leveroptie is aangevinkt. Herzien 2026-08-12 (Brouwers).
  const TRANSPORT_LABEL = { bezorgen: 'Leveren in Nederland of Belgie', zelf: 'Afhalen in Tilburg' };

  // "model|maat|hoofdbord|matras" → variant_id. Matras genormaliseerd: 'incl' voor
  // Marbella/Sevilla, 'gestoffeerd'/'ongestoffeerd' voor Barcelona/Valencia. Hoofdbord
  // = 'na' als het model geen hoofdbord-stap heeft. Null → add-to-cart = "op aanvraag".
  function resolveVariantId() {
    const m = MODELS[state.model];
    if (!m || !state.size) return null;
    const hoofdbord = m.steps.includes('hoofdbord')
      ? (state.hoofdbord || (m.ledIncluded ? 'banen_led' : 'vlak'))
      : 'na';
    const matras = MODEL_MATRAS[state.model]
      ? (state.matras || 'ongestoffeerd')
      : 'incl';
    const key = [state.model, state.size, hoofdbord, matras].join('|');
    return VARIANT_LOOKUP[key] || null;
  }

  const ALL_DIMS = ['model', 'hoofdbord', 'stof', 'poten', 'topper', 'matras', 'size'];
  const state = Object.fromEntries(ALL_DIMS.map(k => [k, null]));
  let activeSteps = ['model'];   // dims die voor het gekozen model gelden (incl. 'model')
  let currentIndex = 0;          // index in activeSteps

  const $$ = (sel, root = container) => root.querySelectorAll(sel);
  const $ = (sel, root = container) => root.querySelector(sel);

  // ── Stappen voor huidig model + progress-bar opbouwen ──
  function setActiveStepsForModel(model) {
    activeSteps = model ? ['model', ...MODELS[model].steps] : ['model'];
    rebuildProgress();
  }

  function rebuildProgress() {
    const ol = $('[data-config-progress]');
    if (!ol) return;
    ol.innerHTML = '';
    // De balk verschijnt pas na de modelkeuze: een preview met 7 stappen oogt als
    // veel werk en klopt niet voor modellen met minder stappen (Valencia: 3).
    if (activeSteps.length <= 1) { ol.classList.add('hidden'); return; }
    ol.classList.remove('hidden');
    const dims = activeSteps;
    dims.forEach((dim, i) => {
      if (i > 0) {
        const sep = document.createElement('li');
        sep.className = 'flex-1 h-px bg-[#0A2540]/15';
        ol.appendChild(sep);
      }
      const li = document.createElement('li');
      li.className = 'flex items-center gap-2';
      li.dataset.stepDim = dim;
      li.innerHTML =
        `<button type="button" data-step-jump="${i}" class="step-dot w-7 h-7 rounded-full flex items-center justify-center font-bold cursor-pointer transition-transform hover:scale-110" aria-label="Ga naar stap ${i + 1}: ${STEP_TITLES[dim]}">${i + 1}</button>` +
        `<span class="hidden sm:inline">${STEP_TITLES[dim]}</span>`;
      ol.appendChild(li);
    });
    ol.querySelectorAll('[data-step-jump]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        // Alleen al onthulde stappen zijn bereikbaar; verder klikken springt
        // naar de eerste nog open stap (geleide flow).
        const target = Math.min(parseInt(btn.dataset.stepJump, 10), firstOpenIndex());
        showStep(target, { scroll: true });
      });
    });
  }

  function stepComplete(i) {
    const dim = activeSteps[i];
    return dim === 'model' ? state.model != null : state[dim] != null;
  }

  // Voert dit model deze maat? In het sheet-model bepaalt het model de geldige maten
  // (geen losse variant-check meer); zo blijven de sheet-maten selecteerbaar terwijl
  // VARIANT_LOOKUP nog leeg is (Fase 1). applySizeFilter verbergt de rest.
  function sizeHasProduct(model, size) {
    return modelAllowsSize(model, size);
  }

  // Verberg maten die dit model niet voert (bv. Valencia = 140/160/180) en
  // markeer maten zonder product (bv. *x210) als niet-bestelbaar i.p.v. kapotte ATC.
  function applySizeFilter(m) {
    const allowed = m.sizes || null;
    $$('.configurator-size').forEach(lbl => {
      const maat = lbl.dataset.maat;
      const ok = !allowed || allowed.includes(maat);
      lbl.classList.toggle('hidden', !ok);
      const input = lbl.querySelector('input');
      if (!ok) {
        if (input && input.checked) { input.checked = false; if (state.size === input.value) state.size = null; }
        return;
      }
      // Maat zichtbaar maar zonder product → uitschakelen + "op aanvraag"-badge
      const hasProduct = sizeHasProduct(state.model, maat);
      const card = lbl.querySelector('div');
      lbl.classList.toggle('opacity-50', !hasProduct);
      lbl.classList.toggle('cursor-not-allowed', !hasProduct);
      lbl.classList.toggle('cursor-pointer', hasProduct);
      if (input) input.disabled = !hasProduct;
      if (card) {
        let badge = card.querySelector('[data-size-onrequest]');
        if (!hasProduct && !badge) {
          badge = document.createElement('p');
          badge.dataset.sizeOnrequest = '1';
          badge.className = 'text-[10px] text-[#C26A4E] mt-1';
          badge.textContent = 'Op aanvraag';
          card.appendChild(badge);
        } else if (hasProduct && badge) {
          badge.remove();
        }
      }
      if (!hasProduct && input && input.checked) { input.checked = false; if (state.size === maat) state.size = null; }
    });
    // 'Op aanvraag'-maten achteraan groeperen zodat de bestelbare maten voorop staan.
    const grid = $('.configurator-size')?.parentElement;
    if (grid) {
      $$('.configurator-size').forEach(lbl => {
        const inp = lbl.querySelector('input');
        if (inp && inp.disabled && !lbl.classList.contains('hidden')) grid.appendChild(lbl);
      });
    }
  }

  // Verberg hoofdbord-opties die dit model niet voert (Barcelona: geen LED, zie
  // MODELS.barcelona.hoofdbordUit). Zet een eventueel al gekozen optie terug, zodat
  // de state nooit op een verborgen waarde blijft staan.
  function applyHoofdbordFilter(m) {
    const uit = m.hoofdbordUit || [];
    $$('.configurator-option input[name="hoofdbord"]').forEach(input => {
      const verborgen = uit.includes(input.value);
      const lbl = input.closest('label');
      if (lbl) lbl.classList.toggle('hidden', verborgen);
      input.disabled = verborgen;
      if (verborgen && input.checked) {
        input.checked = false;
        if (state.hoofdbord === input.value) state.hoofdbord = null;
      }
    });
  }

  // Matras-beelden per model (Brouwers 2026-08-12). De template zet per optie een
  // data-src-<model> met de asset-URL; hier wordt alleen de juiste gekozen. Modellen
  // zonder eigen paar houden het beeld dat al in de `src` staat.
  function applyMatrasBeelden(model) {
    $$('[data-matras-img]').forEach(img => {
      const url = img.dataset['src' + model.charAt(0).toUpperCase() + model.slice(1)];
      if (url && img.getAttribute('src') !== url) img.setAttribute('src', url);
    });
  }

  // Geleide flow (2026-07-05): een stap verschijnt pas als alle voorgaande stappen
  // zijn ingevuld; al ingevulde stappen blijven zichtbaar zodat keuzes aanpasbaar
  // blijven. Nummert de stappen ook per model door (Valencia: matras = Stap 02).
  function firstOpenIndex() {
    const i = activeSteps.findIndex(d => state[d] == null);
    return i === -1 ? activeSteps.length - 1 : i;
  }
  function revealRelevantSteps() {
    const cut = firstOpenIndex();
    let revealed = null;
    $$('.configurator-step').forEach(el => {
      const dim = el.dataset.dim;
      const idx = activeSteps.indexOf(dim);
      const show = dim === 'model' || (idx > -1 && idx <= cut);
      const wasHidden = el.classList.contains('hidden');
      el.classList.toggle('hidden', !show);
      if (show && wasHidden && dim !== 'model') revealed = el;
      // Stapnummer = positie binnen de stappen van dít model (geen vaste nummers/gaten).
      if (idx > -1) {
        const num = el.querySelector('.cfg-idx');
        if (num) num.innerHTML = '<small>Stap</small>' + String(idx + 1).padStart(2, '0');
      }
    });
    return revealed;
  }
  function scrollToStep(el) {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  function showStep(i, opts) {
    currentIndex = i;
    revealRelevantSteps();
    // Progress-stippen: "klaar" = die dim is al gekozen, "actief" = huidige stap.
    $$('[data-config-progress] [data-step-dim]').forEach((el, idx) => {
      const dot = el.querySelector('.step-dot');
      if (!dot) return;
      const dimAt = activeSteps[idx];
      const isActive = idx === i;
      const isDone = dimAt && (dimAt === 'model' ? state.model != null : state[dimAt] != null);
      dot.className = 'step-dot w-7 h-7 rounded-full flex items-center justify-center font-bold cursor-pointer transition-transform hover:scale-110 ' +
        (isActive ? 'bg-[#D4A017] text-[#0A2540]' : isDone ? 'bg-[#0A2540] text-[#D4A017]' : 'bg-[#0A2540]/10 text-[#6E6457]');
    });
    if (opts && opts.scroll) {
      const el = $(`.configurator-step[data-dim="${activeSteps[i]}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // Matras-meerprijs-labels (Barcelona maat-afhankelijk, Valencia +€200). Beweegt mee
  // met de maatkeuze zodat Barcelona het exacte bedrag toont zodra de maat bekend is.
  function updateMatrasLabels() {
    const model = state.model;
    const heeftMatras = model && MODELS[model].steps.includes('matras') && MODEL_MATRAS[model];
    $$('[data-matras-price]').forEach(el => {
      const optie = el.dataset.matrasPrice; // 'gestoffeerd' | 'ongestoffeerd'
      if (!heeftMatras) { el.textContent = ''; return; }
      if (optie === 'ongestoffeerd') { el.textContent = 'geen meerprijs'; return; }
      if (model === 'valencia') { el.textContent = '+€200'; return; }
      if (model === 'barcelona') {
        if (state.size) {
          const { breedte } = splitMaat(state.size);
          const add = MODEL_MATRAS.barcelona[breedte];
          el.textContent = add != null ? '+€' + add.toLocaleString('nl-NL') : '+€500 tot +€800';
        } else {
          el.textContent = '+€500 tot +€800';
        }
      }
    });
  }

  // ── Prijs ──
  // Totaalprijs per maat-tegel: de maat is de grootste prijsknop, dus elke tegel
  // toont het volledige bedrag (bed + evt. high-heel poten) voor de huidige samenstelling.
  function updateSizePrices() {
    $$('.configurator-size [data-size-price]').forEach(el => {
      const lbl = el.closest('.configurator-size');
      const m = state.model && MODELS[state.model];
      if (!m || !lbl) { el.textContent = ''; return; }
      const maat = lbl.dataset.maat;
      if (!modelAllowsSize(state.model, maat)) { el.textContent = ''; return; }
      const bed = bedPrice(state.model, maat, state.hoofdbord, state.matras);
      if (bed == null) { el.textContent = ''; return; }
      const total = bed + potenAdd(state.model, state.poten);
      el.textContent = '€ ' + total.toLocaleString('nl-NL');
    });
  }

  function recalcPrice() {
    updateSizePrices();
    updateMatrasLabels();
    const els = $$('[data-config-price]'); // summary-paneel + mobiele prijsbalk
    const breakdownEl = $('[data-config-breakdown]');
    const m = state.model && MODELS[state.model];
    if (!m) {
      els.forEach(el => { el.textContent = '€ -'; });
      if (breakdownEl) breakdownEl.classList.add('hidden');
      return null;
    }
    // Pas een exacte prijs tonen als ALLE stappen gekozen zijn; tot die tijd een
    // vanaf-bedrag dat meebeweegt met de al gemaakte keuzes.
    const complete = activeSteps.every(d => state[d] != null);

    if (!complete) {
      // Niet-gekozen stappen vallen terug op de basisuitvoering (vlak of LED-basis,
      // matras excl, kleinste maat, geen high-heel).
      const maat = state.size || m.baseMaat;
      const bed = bedPrice(state.model, maat, state.hoofdbord, state.matras);
      const total = (bed != null ? bed : m.base) + potenAdd(state.model, state.poten);
      els.forEach(el => {
        el.innerHTML = '<span class="text-lg" style="font-weight:600;">vanaf</span> € ' + total.toLocaleString('nl-NL');
      });
      if (breakdownEl) breakdownEl.classList.add('hidden');
      return null;
    }

    const bed = bedPrice(state.model, state.size, state.hoofdbord, state.matras);
    if (bed == null) {
      els.forEach(el => { el.textContent = 'Op aanvraag'; });
      if (breakdownEl) breakdownEl.classList.add('hidden');
      return null;
    }
    const poten = potenAdd(state.model, state.poten);
    const total = bed + poten;
    els.forEach(el => { el.textContent = '€ ' + total.toLocaleString('nl-NL'); });
    // Prijsopbouw: basisbed + high-heel poten (losse regel) + overige keuzes (inbegrepen).
    if (breakdownEl) {
      let rows = `<div class="flex justify-between gap-2"><dt>${m.name} · ${LABELS.size[state.size] || state.size}</dt><dd class="text-[#0A2540]">€ ${bed.toLocaleString('nl-NL')}</dd></div>`;
      if (poten) {
        rows += `<div class="flex justify-between gap-2"><dt>${LABELS.poten[state.poten] || 'High-heel poten'}</dt><dd class="text-[#0A2540]">+ € ${poten.toLocaleString('nl-NL')}</dd></div>`;
      }
      ADDON_DIMS.forEach((dim) => {
        if (m.steps.includes(dim) && state[dim] && !(dim === 'poten' && poten)) {
          rows += `<div class="flex justify-between gap-2"><dt>${LABELS[dim]?.[state[dim]] || state[dim]}</dt><dd class="text-[#6E6457]">inbegrepen</dd></div>`;
        }
      });
      breakdownEl.innerHTML = rows;
      breakdownEl.classList.remove('hidden');
    }
    return total;
  }

  function updateSummary(dim, value) {
    const el = $(`[data-summary-${dim}]`);
    if (el) el.textContent = LABELS[dim]?.[value] || value || '-';
  }

  // toon alleen summary-rijen die voor dit model gelden
  function syncSummaryRows() {
    $$('[data-summary-row]').forEach(row => {
      const dim = row.dataset.summaryRow;
      row.classList.toggle('hidden', dim !== 'model' && dim !== 'size' && !(state.model && MODELS[state.model].steps.includes(dim)));
    });
  }

  function checkSubmitReady() {
    const ready = state.model != null && activeSteps.every((dim) => state[dim] != null);
    $$('[data-config-submit]').forEach(btn => { btn.disabled = !ready; });
    // Hint bij de disabled knop: wat is er nog te kiezen? (summary-paneel + mobiele balk)
    const missing = state.model ? activeSteps.filter(d => state[d] == null).map(d => STEP_TITLES[d]) : [];
    const txt = state.model && missing.length ? 'Nog te kiezen: ' + missing.join(', ') : '';
    $$('[data-config-missing]').forEach(el => { el.textContent = txt; el.classList.toggle('hidden', !txt); });
    // Mobiele prijsbalk pas tonen zodra er een model is gekozen; body-class reserveert onderruimte.
    const bar = $('[data-config-bar]');
    if (bar) bar.classList.toggle('hidden', !state.model);
    document.body.classList.toggle('has-config-bar', !!state.model);
  }

  // ── Model-hero + stof-swatch in summary ──
  function swapSummaryVisuals() {
    const heroImg = $('[data-summary-image]');
    if (heroImg && state.model) {
      const cap = state.model.charAt(0).toUpperCase() + state.model.slice(1);
      const src = heroImg.dataset['model' + cap];
      if (heroImg.dataset['full' + cap]) heroImg.dataset.zoomSrc = heroImg.dataset['full' + cap];
      if (src && heroImg.src !== src) {
        heroImg.classList.add('opacity-0');
        const next = new Image();
        next.onload = () => { heroImg.src = src; requestAnimationFrame(() => heroImg.classList.remove('opacity-0')); };
        next.src = src;
      }
    }
    const swatch = $('[data-summary-swatch]');
    if (swatch) {
      const show = state.stof && state.model && MODELS[state.model].steps.includes('stof');
      swatch.classList.toggle('hidden', !show);
      if (show) {
        const opt = $(`.configurator-option input[name="stof"][value="${state.stof}"]`);
        const img = opt?.closest('label')?.querySelector('img');
        if (img) swatch.querySelector('img').src = img.src;
      }
    }
  }

  function persistStateToURL() {
    const params = new URLSearchParams();
    if (state.model) params.set('model', state.model);
    (state.model ? MODELS[state.model].steps : []).forEach(d => { if (state[d]) params.set(d, state[d]); });
    window.history.replaceState({}, '', params.toString() ? `${location.pathname}?${params}` : location.pathname);
  }

  // ── Model selecteren (preset-card of step-1 radio) ──
  function selectModel(model, { advance = false } = {}) {
    if (!MODELS[model]) return;
    state.model = model;
    // Niets voorselecteren: downstream-keuzes leeg + radio's uit, zodat de klant
    // elke stap zelf kiest en het prijsvak start op het vanaf-bedrag van het model.
    ALL_DIMS.filter(d => d !== 'model').forEach(d => {
      state[d] = null;
      $$(`.configurator-option input[name="${d}"]`).forEach(i => { i.checked = false; });
      updateSummary(d, null);
    });
    const m = MODELS[model];
    // model-radio in stap 1 syncen
    const mr = $(`.configurator-option input[name="model"][value="${model}"]`);
    if (mr) mr.checked = true;
    applySizeFilter(m);
    applyHoofdbordFilter(m);
    applyMatrasBeelden(model);
    // Meerprijs-labels hoofdbord per model: bij Sevilla zit LED al in de vanaf-prijs,
    // dus daar is vlak/banen juist goedkoper i.p.v. LED duurder.
    const hbLabels = m.ledIncluded
      ? { vlak: '-€450', banen: '-€250', banen_led: 'inbegrepen' }
      : { vlak: 'geen meerprijs', banen: '+€250', banen_led: '+€450' };
    $$('[data-hb-price]').forEach(p => { p.textContent = hbLabels[p.dataset.hbPrice] || ''; });
    updateSummary('model', model);
    const specEl = $('[data-summary-spec]');
    if (specEl) specEl.textContent = m.spec;
    setActiveStepsForModel(model);
    syncSummaryRows();
    recalcPrice();
    swapSummaryVisuals();
    checkSubmitReady();
    persistStateToURL();
    // Geleide flow: onthul de eerstvolgende stap en maak die actief in de balk;
    // alleen bij een echte gebruikersactie (advance) zacht ernaartoe scrollen.
    const revealed = revealRelevantSteps();
    showStep(firstOpenIndex());
    if (advance && revealed) scrollToStep(revealed);
  }

  // ── Radio-changes ──
  $$('.configurator-option input[type="radio"]').forEach(input => {
    input.addEventListener('change', () => {
      if (input.name === 'model') { selectModel(input.value, { advance: true }); return; }
      state[input.name] = input.value;
      updateSummary(input.name, input.value);
      recalcPrice();
      swapSummaryVisuals();
      checkSubmitReady();
      persistStateToURL();
      // Geleide flow: volgende stap onthullen, balk bijwerken en zacht in beeld brengen.
      const revealed = revealRelevantSteps();
      showStep(firstOpenIndex());
      if (revealed) scrollToStep(revealed);
    });
  });

  // ── Preset-cards (model quick-start bovenaan) ──
  $$('[data-preset]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      selectModel(btn.dataset.preset, { advance: true });
      if (window.trackEvent) window.trackEvent('configurator_model_selected', { model: btn.dataset.preset });
    });
  });

  // ── Nav ──
  $('[data-config-next]')?.addEventListener('click', () => {
    if (!stepComplete(currentIndex)) { alert('Maak eerst een keuze in deze stap.'); return; }
    if (currentIndex < activeSteps.length - 1) showStep(currentIndex + 1);
    else submitConfiguration();
  });
  $('[data-config-prev]')?.addEventListener('click', () => { if (currentIndex > 0) showStep(currentIndex - 1); });

  // ── Lightbox ──
  (function initConfigLightbox() {
    const dialog = document.querySelector('[data-config-lightbox]');
    if (!dialog) return;
    const lightboxImg = dialog.querySelector('img');
    const closeBtn = dialog.querySelector('[data-config-lightbox-close]');
    const open = (src, alt) => {
      lightboxImg.src = src; lightboxImg.alt = alt || '';
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
    };
    const close = () => {
      if (typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
      lightboxImg.src = '';
    };
    container.addEventListener('click', (e) => {
      const img = e.target.closest('[data-config-zoom]');
      if (!img) return;
      e.preventDefault(); e.stopPropagation();
      open(img.dataset.zoomSrc || img.currentSrc || img.src, img.alt);
    });
    closeBtn?.addEventListener('click', close);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
  })();

  async function submitConfiguration(requestTransportPrice = false) {
    if (!activeSteps.every(d => state[d] != null)) { alert('Maak eerst in elke stap een keuze.'); return; }

    // Maat zonder product (bv. *x210) → geen add-to-cart, maar nette melding.
    const variantId = resolveVariantId();
    if (!variantId) {
      alert('Deze maat (' + (LABELS.size[state.size] || state.size) + ') leveren we op aanvraag. ' +
            'Neem contact met ons op via info@boxspringsinspanje.nl, dan stellen we hem voor u samen.');
      return;
    }

    const totalPrice = recalcPrice();
    const m = MODELS[state.model];
    if (!m) return; // defensief: ongeldige/onvolledige state → niet toevoegen
    const transportChoice = container.querySelector('[data-config-transport]:checked');
    const transportValue = transportChoice ? transportChoice.value : 'bezorgen';

    // Eén productregel met de volledige samenstelling als properties.
    // _model + _img (verborgen, '_'-prefix) sturen de titel + modelfoto naar de cart-weergave;
    // de overige properties vormen de zichtbare "Samenstelling" (opbergbox, hoofdbord, stof, …).
    const ONDERBOX_LABEL = { opberg: 'Opbergbox met klep (~900 l)', box30: 'Onderbox 30 cm', box20: 'Onderbox 20 cm' };
    const ob = MODEL_ONDERBOX[state.model];
    const heroImg = $('[data-summary-image]');
    const modelImg = heroImg && heroImg.dataset['model' + state.model.charAt(0).toUpperCase() + state.model.slice(1)];

    // 'Model' is ZICHTBAAR zodat de modelnaam meegaat naar checkout + bestelling (Brouwers).
    // In onze eigen cart-weergave dient 'Model' als titel en filteren we 'm uit de spec-lijst.
    const properties = { 'Model': LABELS.model[state.model] };
    if (modelImg) properties['_img'] = modelImg;
    properties['Maat'] = LABELS.size[state.size] || state.size;
    // Altijd sleutel 'Onderbox' (voorheen 'Opbergbox: Opbergbox met klep' = dubbelop in de cart).
    properties['Onderbox'] = ONDERBOX_LABEL[ob] || ob;
    if (m.steps.includes('hoofdbord')) properties['Hoofdbord'] = LABELS.hoofdbord[state.hoofdbord] || state.hoofdbord;
    properties['Matras'] = LABELS.matras[state.matras || 'gestoffeerd'];
    if (m.steps.includes('stof') && state.stof) properties['Stof'] = LABELS.stof[state.stof] || state.stof;
    if (m.steps.includes('poten') && state.poten) properties['Poten'] = LABELS.poten[state.poten] || state.poten;
    if (m.steps.includes('topper') && state.topper) properties['Topper'] = LABELS.topper[state.topper] || state.topper;

    // Levering is een order-brede keuze → cart is leidend. We zetten het als cart-attribuut
    // (zelfde waarden als de radio's op /cart) i.p.v. als per-item property. Voorkomt de
    // dubbele/los-zwevende levering-keuze tussen configurator en winkelmandje.
    const CART_LEVERING = { bezorgen: 'Leveren in Nederland of Belgie', zelf: 'Afhalen in Tilburg' };
    const leveringLabel = CART_LEVERING[transportValue] || CART_LEVERING.bezorgen;

    const summary = activeSteps.map(d => `${d}:${state[d]}`).join('|');
    // Line-items: het geconfigureerde bed (variant) + optioneel high-heel poten als losse
    // regel (+€100). De potenregel verschijnt alleen als POTEN_ADDON_VARIANT_ID gezet is
    // (Fase 3); zolang die null is reist de potenkeuze puur als property mee (zoals voorheen).
    const potenPrijs = potenAdd(state.model, state.poten);
    const bedPrijs = (typeof totalPrice === 'number' ? totalPrice : 0) - potenPrijs;
    const lineItems = [{ id: variantId, quantity: 1, properties }];
    if (potenPrijs > 0 && POTEN_ADDON_VARIANT_ID) {
      lineItems.push({
        id: POTEN_ADDON_VARIANT_ID, quantity: 1,
        properties: { 'Voor': (LABELS.model[state.model] || '') + ' · ' + (LABELS.size[state.size] || state.size) },
      });
    }


    const btns = $$('[data-config-submit]');
    btns.forEach(btn => { btn.disabled = true; btn.dataset.busy = '1'; });

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: lineItems }),
      });
      if (!res.ok) throw new Error('cart/add gaf status ' + res.status);

      if (window.trackEvent) {
        const gaItems = [{ item_name: LABELS.model[state.model], item_variant: summary, price: bedPrijs, quantity: 1 }];
        if (potenPrijs > 0) gaItems.push({ item_name: 'High-heel poten', item_variant: 'highheel', price: potenPrijs, quantity: 1 });
        window.trackEvent('add_to_cart', { currency: 'EUR', value: totalPrice, items: gaItems, product_category: 'bed' });
      }

      // Levering-keuze als cart-attribuut zetten zodat /cart en checkout dit tonen (cart leidend).
      // CRO 2026-10-06: alleen als de configurator zelf een leverkeuze heeft. Zonder radio laten we het
      // attribuut leeg, zodat templates/cart.liquid en cart-drawer.js de default per land kunnen kiezen
      // (Spaanse marktcontext = Levering naar Spanje) in plaats van altijd Nederland.
      if (transportChoice) try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { Levering: leveringLabel } }),
        });
      } catch (_) { /* niet kritisch: cart toont de keuze sowieso, default = Leveren in Nederland of Belgie */ }

      // Cart-drawer openen (cart-drawer.js luistert op dit event); anders → /cart.
      if (requestTransportPrice === true) {
        // 15-10: eerst de meting laten afleveren; direct navigeren liet ConfiguratorSamengesteld vallen.
        await new Promise((r) => setTimeout(r, 800));
        window.location.href = '/cart?transportprijs=spanje#levering-spanje';
      } else if (window.bxsi && window.bxsi.cartDrawer) {
        document.dispatchEvent(new CustomEvent('bxsi:cart:added'));
      } else {
        window.location.href = '/cart';
      }
      btns.forEach(btn => { btn.disabled = false; delete btn.dataset.busy; });
    } catch (err) {
      console.error('Toevoegen aan winkelmandje mislukt:', err);
      btns.forEach(btn => { btn.disabled = false; delete btn.dataset.busy; });
      alert('Er ging iets mis bij het toevoegen aan uw winkelmandje. Probeer het opnieuw of neem contact met ons op.');
    }
  }
  $$('[data-config-submit]').forEach(btn => btn.addEventListener('click', () => submitConfiguration(btn.dataset.configQuote === 'true')));

  // ── Init vanuit URL (?model=…) ──
  function loadStateFromURL() {
    const params = new URLSearchParams(location.search);
    const model = params.get('model') || params.get('bed_type') || params.get('preset');
    if (model && MODELS[model]) {
      // Geen advance: bij het laden van een (deelbare) URL niet auto-scrollen.
      selectModel(model);
      MODELS[model].steps.forEach(d => {
        const v = params.get(d);
        if (v) {
          const input = $(`.configurator-option input[name="${d}"][value="${v}"]`);
          if (input) { input.checked = true; state[d] = v; updateSummary(d, v); }
        }
      });
      recalcPrice(); swapSummaryVisuals(); checkSubmitReady(); persistStateToURL();
      showStep(firstOpenIndex());
    }
  }

  rebuildProgress();
  showStep(0);
  loadStateFromURL();

  if (window.trackEvent) window.trackEvent('view_item', { item_name: 'Configurator', item_category: 'configurator' });
})();
